/**
 * GrowthIndicator Component
 * Displays percentage change between two values with up/down arrow
 */

import React from "react";
import { Box, Typography } from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import RemoveIcon from "@mui/icons-material/Remove";

/**
 * @param {Object} props
 * @param {number} props.current - Current period value
 * @param {number} props.previous - Previous period value
 * @param {boolean} props.showIndicator - Whether to show the indicator (configurable flag)
 * @param {string} props.size - Size variant: 'small' | 'medium' | 'large'
 */
function GrowthIndicator({
    current,
    previous,
    showIndicator = true,
    size = "medium"
}) {
    if (!showIndicator) {
        return null;
    }

    // Calculate growth
    let percentage = 0;
    let isPositive = true;
    let isZero = true;

    if (previous === 0) {
        if (current > 0) {
            percentage = 100;
            isPositive = true;
            isZero = false;
        }
    } else {
        percentage = ((current - previous) / previous) * 100;
        isPositive = percentage >= 0;
        isZero = percentage === 0;
    }

    // Determine colors
    const getColor = () => {
        if (isZero) return "text.secondary";
        return isPositive ? "success.main" : "error.main";
    };

    // Size configurations
    const sizeConfig = {
        small: {
            iconSize: 12,
            fontSize: "0.75rem",
            padding: "2px 4px"
        },
        medium: {
            iconSize: 14,
            fontSize: "0.8rem",
            padding: "2px 6px"
        },
        large: {
            iconSize: 16,
            fontSize: "0.9rem",
            padding: "4px 8px"
        }
    };

    const config = sizeConfig[size] || sizeConfig.medium;

    // Render appropriate icon
    const renderIcon = () => {
        const iconStyle = {
            fontSize: config.iconSize,
            marginRight: "2px"
        };

        if (isZero) {
            return <RemoveIcon sx={iconStyle} />;
        }
        return isPositive ? (
            <ArrowUpwardIcon sx={iconStyle} />
        ) : (
            <ArrowDownwardIcon sx={iconStyle} />
        );
    };

    return (
        <Box
            sx={{
                display: "inline-flex",
                alignItems: "center",
                backgroundColor: isZero
                    ? "grey.100"
                    : isPositive
                      ? "success.lighter"
                      : "error.lighter",
                color: getColor(),
                borderRadius: "4px",
                padding: config.padding
            }}
        >
            {renderIcon()}
            <Typography
                component="span"
                sx={{
                    fontSize: config.fontSize,
                    fontWeight: 600,
                    color: "inherit"
                }}
            >
                {isZero ? "0%" : `${isPositive ? "+" : "-"}${Math.abs(percentage).toFixed(1)}%`}
            </Typography>
        </Box>
    );
}

export default GrowthIndicator;
